import { API_ENDPOINTS, RELIABILITY_FRAMEWORK, PARAMETER_LINKS, INCIDENTS, HEDWIG } from '../config/api';
import { authFetch } from './authFetch';

// Outages API endpoint
const OUTAGES_API = 'https://reliability-framework-dev-backend.maersk-digital.net/api/outages';

interface PortfolioOverviewResponse {
  alerting: string;
  app_group: string;
  app_id: string;
  app_type: string;
  application: string;
  chaos_app_infra_tested_date: string | null;
  chaos_onboarded: string;
  crawl_completed_for_db: string;
  crawl_completed_for_infra: string;
  crawl_completed_for_mw: string;
  crawl_completed_for_network: string;
  created_at: string;
  cyber_risk_tolerated_mitigated: string;
  deployment_methods: string | null;
  descoped: string;
  em_sem: string;
  eol_components_visibility_roadmap: string | null;
  failover_type: string;
  golden_signals: string | null;
  hedwig_rota: string;
  hosting_scope: string;
  id: number;
  load_test_completed_date: string | null;
  monitoring: string;
  multi_site_dc_multi_zone_cloud_setup: string;
  nfr: string | null;
  nfr_test_completed: string | null;
  owners?: string | null;  // Owner names from mac_info
  owners_raw?: string | null;  // Owner emails from mac_info
  pe_onboarded: string;
  platform: string;
  platform_portfolio: string;
  recovery_sequence: number;
  reference_id: string;
  reliability_patterns: string | null;
  runbooks: string;
  signal_to_noise_ratio: string | null;
  slis: string;
  slos: string | null;
  sre_point_of_contact: string;
  status_page_integration: string;
  support_readiness: string;
  updated_at: string;
  vendor_matrix: string | null;
}

interface ParameterResponse {
  category: string;
  definition: string;
  implementation_guidance: string;
  is_mandatory: boolean;
  parameter_display_name: string;
  parameter_name: string;
  sort_order: number;
  stage: string;
  status: string;
  success_criteria: string;
}

interface ParameterLink {
  id: number;
  link_title: string;
  link_url: string;
  link_type: string;
  link_description: string;
  parameter_name: string;
  is_primary: boolean;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

interface PortfolioOverviewResponseExtended extends PortfolioOverviewResponse {
  owners?: string | null;
  owner_names?: string | null;
}

interface ParameterLinksResponse {
  data: {
    grouped_links: Record<string, ParameterLink[]>;
    links: ParameterLink[];
    reference_id: string;
    total_links: number;
    parameter_status: Record<string, {
      completion_date: string;
      remark: string;
    }>;
  };
  success: boolean;
}

export interface ReliabilityItem {
  completed: boolean;
  definition: string;
  implementationGuidance: string;
  successCriteria: string;
  remarks: string;
  parameterName?: string; // Store parameter_name for API calls
  implementationStatus?: 'Yes' | 'No' | 'NA';
  completion_date?: string; // Completion date at parameter level
  excludeFromCalculation?: boolean; // Mark items that should not count toward progress
  links?: { title: string; url: string }[];
}

export interface ApplicationData {
  name: string;
  sreContact: string;
  emSem: string;
  owners?: string;
  ownersRaw?: string;  // Owner emails for tooltip
  hosting: string;
  appType: string;
  platform: string;
  platformPortfolio: string;
  crawl: Record<string, ReliabilityItem>;
  walk: Record<string, ReliabilityItem>;
  run: Record<string, ReliabilityItem>;
  lastUpdated: string;
  appId: string;
  referenceId: string;
}

/**
 * Fetch portfolio overview data from the API
 */
export const fetchPortfolioOverview = async (): Promise<PortfolioOverviewResponse[]> => {
  try {
    const response = await authFetch(RELIABILITY_FRAMEWORK.PORTFOLIO_OVERVIEW);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    // Handle wrapped response format
    const data = result.data || result;
    return Array.isArray(data) ? data : [data];
  } catch (error) {
    console.error('Error fetching portfolio overview:', error);
    throw new Error(`Failed to fetch portfolio overview: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Fetch reliability parameters from the API
 */
export const fetchReliabilityParameters = async (): Promise<ParameterResponse[]> => {
  try {
    const response = await authFetch(RELIABILITY_FRAMEWORK.PARAMETERS);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    // Handle the nested response structure
    let parameters: ParameterResponse[] = [];
    
    if (result.data && result.data.parameters_by_stage) {
      // Flatten the parameters from all stages
      const stageData = result.data.parameters_by_stage;
      Object.keys(stageData).forEach(stage => {
        if (Array.isArray(stageData[stage])) {
          parameters = parameters.concat(stageData[stage]);
        }
      });
    } else if (Array.isArray(result.data)) {
      parameters = result.data;
    } else if (Array.isArray(result)) {
      parameters = result;
    }
    
    return parameters;
  } catch (error) {
    console.error('Error fetching reliability parameters:', error);
    throw new Error(`Failed to fetch reliability parameters: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Fetch parameter links for a specific reference ID
 */
export const fetchParameterLinks = async (referenceId: string): Promise<ParameterLinksResponse> => {
  try {
    const response = await authFetch(PARAMETER_LINKS(referenceId));
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    return result;
  } catch (error) {
    console.error(`Error fetching parameter links for ${referenceId}:`, error);
    throw new Error(`Failed to fetch parameter links: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Load parameter links for a specific application and update its metrics
 */
export const loadParameterLinksForApplication = async (
  applications: Record<string, ApplicationData>, 
  referenceId: string
): Promise<Record<string, ApplicationData>> => {
  try {
    console.log(`🔗 Loading parameter links for reference ID: ${referenceId}`);
    
    // Fetch parameter links for this application
    const linksResponse = await fetchParameterLinks(referenceId);
    const parameterLinks = linksResponse.data.grouped_links || {};
    const parameterStatus = linksResponse.data.parameter_status || {};
    
    console.log(`📎 Fetched ${Object.keys(parameterLinks).length} parameter link groups`);
    console.log(`📋 Fetched parameter status for ${Object.keys(parameterStatus).length} parameters`);
    
    // Find and update the application with the reference ID
    const updatedApplications = { ...applications };
    
    Object.keys(updatedApplications).forEach(appKey => {
      const app = updatedApplications[appKey];
      if (app.referenceId === referenceId) {
        console.log(`🔄 Updating links for application: ${app.name}`);
        
        // Update crawl metrics with links and parameter status
        const updatedCrawl = { ...app.crawl };
        Object.keys(updatedCrawl).forEach(metricKey => {
          const metric = updatedCrawl[metricKey];
          const paramLinks = parameterLinks[metric.parameterName || ''] || [];
          const paramStatus = parameterStatus[metric.parameterName || ''];
          updatedCrawl[metricKey] = {
            ...metric,
            links: paramLinks.map((link: ParameterLink) => ({
              title: link.link_title,
              url: link.link_url
            })),
            remarks: paramStatus?.remark || metric.remarks || '',
            completion_date: paramStatus?.completion_date || metric.completion_date || ''
          };
        });
        
        // Update walk metrics with links and parameter status
        const updatedWalk = { ...app.walk };
        Object.keys(updatedWalk).forEach(metricKey => {
          const metric = updatedWalk[metricKey];
          const paramLinks = parameterLinks[metric.parameterName || ''] || [];
          const paramStatus = parameterStatus[metric.parameterName || ''];
          updatedWalk[metricKey] = {
            ...metric,
            links: paramLinks.map((link: ParameterLink) => ({
              title: link.link_title,
              url: link.link_url
            })),
            remarks: paramStatus?.remark || metric.remarks || '',
            completion_date: paramStatus?.completion_date || metric.completion_date || ''
          };
        });
        
        // Update run metrics with links and parameter status
        const updatedRun = { ...app.run };
        Object.keys(updatedRun).forEach(metricKey => {
          const metric = updatedRun[metricKey];
          const paramLinks = parameterLinks[metric.parameterName || ''] || [];
          const paramStatus = parameterStatus[metric.parameterName || ''];
          updatedRun[metricKey] = {
            ...metric,
            links: paramLinks.map((link: ParameterLink) => ({
              title: link.link_title,
              url: link.link_url
            })),
            remarks: paramStatus?.remark || metric.remarks || '',
            completion_date: paramStatus?.completion_date || metric.completion_date || ''
          };
        });
        
        // Update the application
        updatedApplications[appKey] = {
          ...app,
          crawl: updatedCrawl,
          walk: updatedWalk,
          run: updatedRun
        };
        
        console.log(`✅ Updated ${app.name} with parameter links`);
      }
    });
    
    return updatedApplications;
  } catch (error) {
    console.error(`❌ Error loading parameter links for ${referenceId}:`, error);
    return applications; // Return original if error
  }
};

/**
 * Transform API data to the format expected by the ReliabilityAssessment component
 */
export const transformApiDataToApplications = async (): Promise<Record<string, ApplicationData>> => {
  try {
    console.log('🔄 Starting data transformation...');
    
    // Fetch both portfolio overview and parameters
    const [portfolioData, parametersData] = await Promise.all([
      fetchPortfolioOverview(),
      fetchReliabilityParameters()
    ]);

    console.log('📊 Portfolio data received:', portfolioData.length, 'applications');
    console.log('📋 Parameters data received:', parametersData.length, 'parameters');

    // Group parameters by stage
    const parametersByStage = parametersData.reduce((acc, param) => {
      const stage = param.stage.toLowerCase();
      if (!acc[stage]) {
        acc[stage] = [];
      }
      acc[stage].push(param);
      return acc;
    }, {} as Record<string, ParameterResponse[]>);

    console.log('📂 Parameters grouped by stage:', Object.keys(parametersByStage));

    // Transform portfolio data to applications format
    const applications: Record<string, ApplicationData> = {};

    // Process each application without fetching links (for performance)
    portfolioData.forEach((appRaw, index) => {
      // support owner fields returned from backend (owner_names / owners) and legacy em_sem
      const app = appRaw as PortfolioOverviewResponseExtended & any;
      const appKey = `app_${app.app_id || index}`;
      console.log(`🏗️ Processing app ${index + 1}/${portfolioData.length}: ${app.application}`);
      
      // Create reliability items for each stage based on API data
      const crawlItems: Record<string, ReliabilityItem> = {};
      const walkItems: Record<string, ReliabilityItem> = {};
      const runItems: Record<string, ReliabilityItem> = {};

      // Map crawl stage parameters
      if (parametersByStage.crawl) {
        parametersByStage.crawl
          .sort((a, b) => a.sort_order - b.sort_order)
          .forEach(param => {
            // Get the actual completion status from the app data
            const paramValue = (app as any)[param.parameter_name];
            const isCompleted = paramValue === 'Yes' || paramValue === '1' || paramValue === true;
            
            // Determine implementation status: handle "NA", "Yes", and "No" cases
            let implementationStatus: 'Yes' | 'No' | 'NA' = 'No';
            if (paramValue === 'NA') {
              implementationStatus = 'NA';
            } else if (isCompleted) {
              implementationStatus = 'Yes';
            }
            
            crawlItems[param.parameter_display_name] = {
              completed: isCompleted, // Don't treat NA as completed for calculation
              implementationStatus: implementationStatus,
              parameterName: param.parameter_name, // Store parameter_name for API calls
              definition: param.definition,
              implementationGuidance: param.implementation_guidance,
              successCriteria: param.success_criteria,
              remarks: '',
              links: [] // Will be loaded on-demand
            };
          });
      }

      // Map walk stage parameters
      if (parametersByStage.walk) {
        parametersByStage.walk
          .sort((a, b) => a.sort_order - b.sort_order)
          .forEach(param => {
            const paramValue = (app as any)[param.parameter_name];
            const isCompleted = paramValue === 'Yes' || paramValue === '1' || paramValue === true;
            
            // Determine implementation status: handle "NA", "Yes", and "No" cases
            let implementationStatus: 'Yes' | 'No' | 'NA' = 'No';
            if (paramValue === 'NA') {
              implementationStatus = 'NA';
            } else if (isCompleted) {
              implementationStatus = 'Yes';
            }
            
            // Check if this parameter should be excluded from Walk phase calculations
            const excludedWalkParameters = ['pe_onboarded', 'load_test_completed_date', 'chaos_onboarded'];
            const excludeFromCalculation = excludedWalkParameters.includes(param.parameter_name);
            
            walkItems[param.parameter_display_name] = {
              completed: isCompleted, // Don't treat NA as completed for calculation
              implementationStatus: implementationStatus,
              parameterName: param.parameter_name, // Store parameter_name for API calls
              definition: param.definition,
              implementationGuidance: param.implementation_guidance,
              successCriteria: param.success_criteria,
              remarks: '',
              links: [], // Will be loaded on-demand
              excludeFromCalculation: excludeFromCalculation
            };
          });
      }

      // Map run stage parameters
      if (parametersByStage.run) {
        parametersByStage.run
          .sort((a, b) => a.sort_order - b.sort_order)
          .forEach(param => {
            const paramValue = (app as any)[param.parameter_name];
            const isCompleted = paramValue === 'Yes' || paramValue === '1' || paramValue === true;
            
            // Determine implementation status: handle "NA", "Yes", and "No" cases
            let implementationStatus: 'Yes' | 'No' | 'NA' = 'No';
            if (paramValue === 'NA') {
              implementationStatus = 'NA';
            } else if (isCompleted) {
              implementationStatus = 'Yes';
            }
            
            runItems[param.parameter_display_name] = {
              completed: isCompleted, // Don't treat NA as completed for calculation
              implementationStatus: implementationStatus,
              parameterName: param.parameter_name, // Store parameter_name for API calls
              definition: param.definition,
              implementationGuidance: param.implementation_guidance,
              successCriteria: param.success_criteria,
              remarks: '',
              links: [] // Will be loaded on-demand
            };
          });
      }

      applications[appKey] = {
        name: app.application,
        sreContact: app.sre_point_of_contact,
        owners: app.owners || app.em_sem || '',
        ownersRaw: app.owners_raw || app.em_sem || '',  // Capture owner emails
        emSem: app.em_sem,
        hosting: app.hosting_scope,
        appType: app.app_type === '0' ? 'Web Application' : 'Other',
        platform: app.platform,
        platformPortfolio: app.platform_portfolio,
        crawl: crawlItems,
        walk: walkItems,
        run: runItems,
        lastUpdated: new Date(app.updated_at).toLocaleDateString(),
        appId: app.app_id,
        referenceId: app.reference_id
      };

      console.log(`✅ Processed ${app.application}: Crawl(${Object.keys(crawlItems).length}) Walk(${Object.keys(walkItems).length}) Run(${Object.keys(runItems).length})`);
    });

    console.log('🎯 Transformation complete:', Object.keys(applications).length, 'applications ready');
    return applications;
  } catch (error) {
    console.error('❌ Error transforming API data:', error);
    throw new Error(`Failed to transform API data: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Get applications grouped by platform portfolio and platform
 */
export const getApplicationsGroupedByPlatform = async (): Promise<{
  [platformPortfolio: string]: {
    [platform: string]: ApplicationData[]
  }
}> => {
  try {
    const applications = await transformApiDataToApplications();
    const grouped: { [platformPortfolio: string]: { [platform: string]: ApplicationData[] } } = {};

    Object.values(applications).forEach(app => {
      if (!grouped[app.platformPortfolio]) {
        grouped[app.platformPortfolio] = {};
      }
      if (!grouped[app.platformPortfolio][app.platform]) {
        grouped[app.platformPortfolio][app.platform] = [];
      }
      grouped[app.platformPortfolio][app.platform].push(app);
    });

    return grouped;
  } catch (error) {
    console.error('Error grouping applications by platform:', error);
    throw error;
  }
};

/**
 * Save application data (for future use with backend save endpoint)
 */
export const saveApplicationData = async (appId: string, applicationData: ApplicationData): Promise<void> => {
  try {
    // This is a placeholder for future implementation
    // You would implement the actual save API call here
    console.log('Saving application data:', { appId, applicationData });
    
    // For now, just store in localStorage as a fallback
    const savedData = localStorage.getItem('reliability_assessment_data');
    const existingData = savedData ? JSON.parse(savedData) : {};
    existingData[appId] = applicationData;
    localStorage.setItem('reliability_assessment_data', JSON.stringify(existingData));
  } catch (error) {
    console.error('Error saving application data:', error);
    throw new Error(`Failed to save application data: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Load saved application data from localStorage (for future use)
 */
export const loadSavedApplicationData = (): Record<string, Partial<ApplicationData>> => {
  try {
    const savedData = localStorage.getItem('reliability_assessment_data');
    return savedData ? JSON.parse(savedData) : {};
  } catch (error) {
    console.error('Error loading saved application data:', error);
    return {};
  }
};

// New interfaces for incident APIs
interface IncidentResponse {
  assignment_group_display_value: string;
  category_display_value: string;
  caused_by_display_value: string;
  child_incidents: string;
  close_code: string;
  cmdb_ci_display_value: string;
  cmdb_ci_u_application_id: string;
  cmdb_ci_u_environment_display_value: string;
  cmdb_ci_u_environment_value: string;
  cmdb_ci_u_owning_business_unit_display_value: string;
  contact_type_display_value: string;
  created_at: string;
  id: number;
  impact: string;
  location_display_value: string;
  number: string;
  opened_by_display_value: string;
  outage_duration: number | null;
  outage_number: string | null;
  platform_name: string;
  portfolio_name: string;
  priority: string;
  resolved_at: string | null;
  short_description: string;
  state_display_value: string;
  subcategory_display_value: string;
  sys_created_on_display_value: string;
  sys_created_on_value: string;
  u_client_name_display_value: string;
  u_owner_display_value: string;
  updated_at: string;
  urgency: string;
}

interface IncidentsApiResponse {
  count: number;
  data: IncidentResponse[];
}

/**
 * Fetch P1/P2 incidents from the API
 */
export const fetchP1P2Incidents = async (): Promise<IncidentResponse[]> => {
  try {
    const response = await authFetch(INCIDENTS.P1_P2);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result: IncidentsApiResponse = await response.json();
    return result.data || [];
  } catch (error) {
    console.error('Error fetching P1/P2 incidents:', error);
    throw new Error(`Failed to fetch P1/P2 incidents: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Fetch P3/P4 incidents from the API
 */
export const fetchP3P4Incidents = async (): Promise<IncidentResponse[]> => {
  try {
    const response = await authFetch(INCIDENTS.P3_P4);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result: IncidentsApiResponse = await response.json();
    return result.data || [];
  } catch (error) {
    console.error('Error fetching P3/P4 incidents:', error);
    throw new Error(`Failed to fetch P3/P4 incidents: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Fetch P1/P2 incidents with date range filter
 */
export const fetchP1P2IncidentsWithDateRange = async (startTime: string, endTime: string): Promise<IncidentResponse[]> => {
  try {
    const response = await authFetch(INCIDENTS.P1_P2, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        start_time: startTime,
        end_time: endTime
      })
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result: IncidentsApiResponse = await response.json();
    return result.data || [];
  } catch (error) {
    console.error('Error fetching P1/P2 incidents with date range:', error);
    throw new Error(`Failed to fetch P1/P2 incidents with date range: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Fetch P3/P4 incidents with date range filter
 */
export const fetchP3P4IncidentsWithDateRange = async (startTime: string, endTime: string): Promise<IncidentResponse[]> => {
  try {
    const response = await authFetch(INCIDENTS.P3_P4, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        start_time: startTime,
        end_time: endTime
      })
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result: IncidentsApiResponse = await response.json();
    return result.data || [];
  } catch (error) {
    console.error('Error fetching P3/P4 incidents with date range:', error);
    throw new Error(`Failed to fetch P3/P4 incidents with date range: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Fetch all incidents (P1-P4) from both APIs
 */
export const fetchAllIncidents = async (): Promise<IncidentResponse[]> => {
  try {
    const [p1p2Incidents, p3p4Incidents] = await Promise.all([
      fetchP1P2Incidents(),
      fetchP3P4Incidents()
    ]);
    
    return [...p1p2Incidents, ...p3p4Incidents];
  } catch (error) {
    console.error('Error fetching all incidents:', error);
    throw new Error(`Failed to fetch incidents: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Fetch all incidents (P1-P4) with date range filter
 */
export const fetchAllIncidentsWithDateRange = async (startTime: string, endTime: string): Promise<IncidentResponse[]> => {
  try {
    const [p1p2Incidents, p3p4Incidents] = await Promise.all([
      fetchP1P2IncidentsWithDateRange(startTime, endTime),
      fetchP3P4IncidentsWithDateRange(startTime, endTime)
    ]);
    
    return [...p1p2Incidents, ...p3p4Incidents];
  } catch (error) {
    console.error('Error fetching all incidents with date range:', error);
    throw new Error(`Failed to fetch incidents with date range: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Get unique portfolio names from portfolio overview for dropdown
 */
export const fetchPortfolioNames = async (): Promise<string[]> => {
  try {
    const portfolioData = await fetchPortfolioOverview();
    const uniquePortfolios = Array.from(new Set(portfolioData.map(item => item.platform_portfolio)));
    return uniquePortfolios.sort();
  } catch (error) {
    console.error('Error fetching portfolio names:', error);
    throw new Error(`Failed to fetch portfolio names: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

// Hedwig API interfaces
interface AlertType {
  alert_type: string;
  avg_resolution: string;
  firing: number;
  percentage_total: string;
  resolved: number;
  total: number;
}

interface DailyTrend {
  avg_resolution_time: string;
  day: string;
  firing: number;
  resolution_rate: string;
  resolved: number;
  total_alerts: number;
}

interface ResolutionCategory {
  category: string;
  count: number;
  percentage: string;
}

interface HedwigAnalysisRequest {
  scope_name: string;
  start_time: string;
  end_time: string;
  channel?: string;
}

// Outages API interfaces
interface OutageResponse {
  begin_time: string;
  cmdb_ci_display_value: string;
  cmdb_ci_sys_class_name: string;
  duration: number;
  end_time: string;
  number: string;
  short_description: string;
  task_number_display_value: string;
}

interface OutagesApiResponse {
  applied_filters: {
    end_date?: string;
    start_date?: string;
  };
  count: number;
  data: OutageResponse[];
  debug_info: any;
  filters: any;
  success: boolean;
  timestamp: string;
}

interface HedwigAnalysisResponse {
  data?: {
    alert_type_analysis: {
      alert_types: AlertType[];
      total_alerts: number;
    };
    daily_performance_trends: {
      daily_trends: DailyTrend[];
      total_days: number;
    };
    key_performance_indicators: {
      context: {
        analysis_period: string;
        firing_alerts: number;
        resolved_alerts: number;
        total_alerts: number;
      };
      kpis: {
        "95th Percentile Resolution": string;
        "Alert Resolution Rate": string;
        "Daily Alert Frequency": string;
        "Mean Time To Resolution (MTTR)": string;
        "Median Resolution Time": string;
      };
    };
    resolution_time_analysis: {
      categories: ResolutionCategory[];
      total_resolved: number;
    };
    summary: {
      analysis_scope: string;
      avg_daily_alerts: number;
      data_freshness: string;
      time_period: string;
      top_alert_type: string;
      total_alerts_analyzed: number;
    };
    sync_execution: {
      end_time: string;
      execution_time_seconds: number;
      scope_name: string;
      start_time: string;
      sync_output: string;
      sync_status: string;
    };
  };
  message?: string;
  success: boolean;
  timestamp?: string;
  // Error fields for scope not found and other errors
  error?: string;
  scope_not_found?: boolean;
}

/**
 * Fetch outages data with date range filter
 */
export const fetchOutages = async (startTime: string, endTime: string): Promise<OutageResponse[]> => {
  try {
    console.log('🔄 Fetching outages data from:', OUTAGES_API);
    console.log('📅 Date range:', { start_time: startTime, end_time: endTime });
    
    const response = await authFetch(OUTAGES_API, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        start_time: startTime,
        end_time: endTime
      }),
    });

    console.log('📡 Outages API Response status:', response.status);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result: OutagesApiResponse = await response.json();
    console.log('✅ Outages API Response received:', {
      count: result.count,
      applied_filters: result.applied_filters,
      success: result.success,
      sample_data: result.data?.slice(0, 2)
    });
    
    return result.data || [];
  } catch (error) {
    console.error('❌ Error fetching outages data:', error);
    throw new Error(`Failed to fetch outages data: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Fetch Hedwig comprehensive analysis
 */
export const fetchHedwigAnalysis = async (request: HedwigAnalysisRequest): Promise<HedwigAnalysisResponse> => {
  try {
    console.log('🚀 Making API call to Hedwig with request:', request);
    console.log('🎯 API URL:', HEDWIG.COMPREHENSIVE_ANALYSIS);
    console.log('📦 Request body:', JSON.stringify(request, null, 2));

    const response = await authFetch(HEDWIG.COMPREHENSIVE_ANALYSIS, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    console.log('📡 API Response status:', response.status);
    console.log('📋 API Response headers:', response.headers);

    if (!response.ok) {
      // Handle specific error cases like 404 for scope not found
      if (response.status === 404) {
        const errorData = await response.json();
        console.log('🚫 Scope not found error:', errorData);
        return errorData; // Return the error response instead of throwing
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('✅ API Response data received:', data);
    console.log('🔍 Summary scope from response:', data?.data?.summary?.analysis_scope);
    
    return data;
  } catch (error) {
    console.error('❌ Error fetching Hedwig analysis:', error);
    throw new Error(`Failed to fetch Hedwig analysis: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};